import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useApp } from '../context/AppContext';

interface OnboardingSlide {
  title: string;
  description: string;
  image: string;
}

const slides: OnboardingSlide[] = [
  {
    title: 'Discover Local Sales',
    description: 'Find yard sales, garage sales, and estate sales happening near you.',
    image: 'https://images.unsplash.com/photo-1555529733-0e67056058e1?auto=format&fit=crop&q=80&w=600',
  },
  {
    title: 'Never Miss a Sale',
    description: 'Get directions, save your favorites, and add sales to your calendar.',
    image: 'https://images.unsplash.com/photo-1520006403909-838d6b92c22e?auto=format&fit=crop&q=80&w=600',
  },
  {
    title: 'Explore Your Area',
    description: 'Browse sales by city and discover hidden gems in your neighborhood.',
    image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80&w=600',
  },
];

const OnboardingScreen: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const { setOnboardingCompleted } = useApp();

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    setOnboardingCompleted(true);
    navigate('/home');
  };

  const handleSkip = () => {
    handleComplete();
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {/* Skip Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
        <Button
          onClick={handleSkip}
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          Skip
        </Button>
      </Box>

      {/* Slide Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 4,
        }}
      >
        <Box
          component="img"
          src={slides[currentSlide].image}
          alt={slides[currentSlide].title}
          sx={{
            width: '100%',
            maxWidth: 320,
            height: 240,
            objectFit: 'cover',
            borderRadius: 3,
            mb: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        />

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            textAlign: 'center',
            mb: 2,
            color: 'text.primary',
          }}
        >
          {slides[currentSlide].title}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            maxWidth: 300,
            lineHeight: 1.6,
          }}
        >
          {slides[currentSlide].description}
        </Typography>
      </Box>

      {/* Dots Indicator */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mb: 3,
        }}
      >
        {slides.map((_, index) => (
          <Box
            key={index}
            sx={{
              width: currentSlide === index ? 24 : 8,
              height: 8,
              borderRadius: 4,
              bgcolor: currentSlide === index ? 'primary.main' : 'grey.300',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </Box>

      {/* Bottom Action */}
      <Box sx={{ p: 3, pb: 4 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleNext}
          sx={{
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 3,
          }}
        >
          {currentSlide === slides.length - 1 ? 'Get Started' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default OnboardingScreen;
